/**
 * Builds Puck's `Config` from our block specs.
 *
 * The important part is `render`: it calls `spec.toIR(props, ctx)` and hands the result to
 * `renderIR`. That is the same `toIR` the XscpData emitter calls, which is what makes the
 * preview and the Webflow export structurally identical by construction rather than by
 * discipline.
 */

import type { ReactElement } from 'react';
import type { Config, ComponentConfig } from '@puckeditor/core';
import type { BlockSpec, IRContext, IRSlot } from '@lp/shared';
import { allBlockSpecs, groupOf } from '@lp/blocks';
import { renderIR } from '@lp/blocks/render';
import { fieldSpecToPuck } from './fieldSpecToPuck.js';
import { iconNode } from '../icons.js';
import { EmptyState } from './EmptyState.js';
import { useDocStore } from '../store/docStore.js';

/**
 * Puck hands slot props back as components. `SlotComponent` accepts DropZone props, so a
 * block's `IRSlot` resolves to `<Slot minEmptyHeight=… />` here and to the child blocks'
 * own IR in the exporter — two resolvers, one tree shape.
 */
type SlotComponent = (props?: Record<string, unknown>) => ReactElement | null;

export interface BuildOptions {
  docType: 'landing' | 'article';
  geo: 'uk' | 'us' | 'both';
}

export function buildPuckConfig(opts: BuildOptions): Config {
  const specs = allBlockSpecs();

  const components = Object.fromEntries(
    specs.map((spec) => {
      const typedSpec = spec as BlockSpec<Record<string, unknown>>;

      const config: ComponentConfig = {
        label: typedSpec.label,
        fields: Object.fromEntries(
          Object.entries(typedSpec.fields).map(([key, fieldSpec]) => [
            key,
            fieldSpecToPuck(fieldSpec),
          ]),
        ),
        defaultProps: typedSpec.defaultProps,

        // Lets a block hide controls that would do nothing for the current props — the
        // Columns block shows only the column slots its split actually uses.
        ...(typedSpec.fieldVisibility
          ? {
              resolveFields: (data: { props?: Record<string, unknown> }) => {
                const visible = typedSpec.fieldVisibility!(
                  (data.props ?? typedSpec.defaultProps) as Record<string, unknown>,
                );
                return Object.fromEntries(
                  Object.entries(typedSpec.fields)
                    .map(([key, fieldSpec]) => [key, fieldSpecToPuck(fieldSpec)] as const)
                    .filter(([key]) => visible[key] !== false)
                    .map(([key, field]) => [key, field]),
                );
              },
            }
          : {}),

        render: (props: Record<string, unknown>) => {
          const ctx: IRContext = {
            docType: opts.docType,
            geo: opts.geo,
            // Puck does not tell a component its index, and the hero is the only block that
            // cares. Treating it as first is right in every realistic layout and errs toward
            // eager-loading a hero image, which is the safe direction for LCP.
            isFirstSection: typedSpec.id === 'HeroSplit',
          };

          const ir = typedSpec.toIR(props, ctx);

          return (
            <>
              {renderIR(ir, {
                icon: (name) => iconNode(name),
                // The layout classes go on Puck's drop zone itself. Wrapping it instead
                // would make the wrapper the container's only child, collapsing a grid
                // into a single cell.
                slot: (name: string, className?: string) => {
                  const Slot = props[name] as unknown as SlotComponent | undefined;
                  return Slot ? <Slot className={className} minEmptyHeight={120} /> : null;
                },
              })}
            </>
          ) as ReactElement;
        },
      } as ComponentConfig;

      return [typedSpec.id, config];
    }),
  );

  return {
    components,
    root: {
      // Puck always passes a DropZone as `children`, so inspecting it tells you nothing about
      // whether the page has content. Read the document instead.
      render: ({ children }: { children?: React.ReactNode }) => <PageRoot>{children}</PageRoot>,
    },
    categories: {
      layout: {
        title: 'Layout',
        defaultExpanded: true,
        components: specs.filter((s) => groupOf(s.id) === 'layout').map((s) => s.id),
      },
      content: {
        title: 'Content',
        defaultExpanded: true,
        components: specs.filter((s) => groupOf(s.id) === 'content').map((s) => s.id),
      },
      presets: {
        // Presets emit exactly the same utility classes as a hand-built section, so they are
        // a head start rather than a black box — drop one in and take it apart.
        title: 'Presets',
        defaultExpanded: false,
        components: specs.filter((s) => groupOf(s.id) === 'presets').map((s) => s.id),
      },
    },
  } as Config;
}

/**
 * The canvas root.
 *
 * The empty state sits *behind* Puck's drop zone rather than replacing it — the drop zone has
 * to stay mounted or there is nowhere to drop the first block.
 */
function PageRoot({ children }: { children?: React.ReactNode }): ReactElement {
  const isEmpty = useDocStore((s) => s.puck.content.length === 0);
  return (
    <div className={`lp-page${isEmpty ? ' lp-page--empty' : ''}`}>
      {isEmpty && <EmptyState />}
      {children}
    </div>
  );
}

/** Re-exported so the icon module is not tree-shaken out of the editor bundle. */
export { iconNode };

/** Typed helper for the one node kind the editor resolves differently from the exporter. */
export type EditorSlot = IRSlot;
